/**
 * Stream configuration form: metadata, operator assignment, messaging fields, LinkedIn URLs, action limits, danger zone.
 *
 * Where: Settings tab on `StreamDetails`.
 * Uses: `stream`/`team` APIs via React Query; `ActionLimitsControl`; `useAuth` for permissions.
 * Behavior: save/reset patterns with validation; may open nested dialogs for destructive actions.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { Save as SaveIcon } from '@mui/icons-material';
import { teamApi } from '../../api/team';
import { useAuth } from '@/hooks/useAuth';
import type { StreamDetails } from '../../api/stream';
import type { TeamItem } from '@/types/team';

interface StreamSettingsProps {
  stream: StreamDetails;
}

interface StreamUpdateData {
  name: string;
  status: string;
  notes?: string;
  ai_theme_plot?: string;
  team_uuid?: string;
  operator_uuid?: string;
  creator_uuid?: string;
  start_date?: string;
  end_date?: string;
}

interface FormErrors {
  name?: string;
  status?: string;
  notes?: string;
  ai_theme_plot?: string;
  team_uuid?: string;
  operator_uuid?: string;
  creator_uuid?: string;
  start_date?: string;
  end_date?: string;
}


const STREAM_STATUSES = [
  { value: 'RUNNING', label: 'Running' },
  { value: 'PAUSED', label: 'Paused' },
  { value: 'DONE', label: 'Done' },
  { value: 'WAITING', label: 'Waiting' },
  { value: 'WITHDRAW', label: 'Withdraw' },
  { value: 'ARCHIVE', label: 'Archive' },
];

export const StreamSettings: React.FC<StreamSettingsProps> = ({ stream }) => {
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const [formData, setFormData] = useState<StreamUpdateData>({
    name: stream.name || '',
    status: stream.status || 'WAITING',
    notes: stream.notes || '',
    ai_theme_plot: stream.ai_theme_plot || '',
    team_uuid: stream.team?.key,
    operator_uuid: stream.operator?.key,
    creator_uuid: undefined, // Will be set after verifying it exists in availableCreators
    start_date: stream.start_date || undefined,
    end_date: stream.end_date || undefined,
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  const isAdmin = currentUser?.is_staff || currentUser?.is_superuser;

  // Load teams where user is owner or manager
  const { data: teamsData, isLoading: isLoadingTeams } = useQuery({
    queryKey: ['user-teams', currentUser?.key],
    queryFn: () => teamApi.getTeams({ manager_or_owner_uuid: currentUser?.key, max_results: 1000 }),
    enabled: !!currentUser,
  });

  // Get available teams, ensuring current stream's team is included even before query loads
  const baseTeams: TeamItem[] = teamsData?.output?.items || [];
  const availableTeams: TeamItem[] = [...baseTeams];
  // Always include current stream's team if it exists and isn't already in the list
  // This ensures the select shows the correct value even before data loads
  if (stream.team && !availableTeams.find((t) => t.key === stream.team?.key)) {
    // Create a compatible TeamItem from stream.team
    const streamTeamItem: TeamItem = {
      key: stream.team.key,
      name: stream.team.name,
      created_at: '', // Required field, but not used for display
      status: 'ACTIVE', // Required field, but not used for display
    };
    availableTeams.unshift(streamTeamItem);
  }
  const hasTeams = availableTeams.length > 0;

  // Load team members when team is selected
  const { data: teamRolesData, isLoading: isLoadingTeamRoles } = useQuery({
    queryKey: ['team-roles', formData.team_uuid],
    queryFn: () => teamApi.getTeamRoles(formData.team_uuid!),
    enabled: !!formData.team_uuid,
  });

  const teamMembers = teamRolesData?.output?.items || [];

  // Load all users for admin/staff when no team selected
  const { data: allUsersData, isLoading: isLoadingAllUsers } = useQuery({
    queryKey: ['all-users', currentUser?.key],
    queryFn: () => teamApi.searchAllUsers({ max_results: 1000 }),
    enabled: !!currentUser && !formData.team_uuid && isAdmin,
  });

  const allUsers = allUsersData?.output?.items || [];

  // Get available operators based on context
  const getAvailableOperators = () => {
    let operators: any[] = [];
    
    if (formData.team_uuid) {
      // If team selected, return team members + owner
      const memberUsers = teamMembers.map((role: any) => role.user).filter(Boolean);
      const team = availableTeams.find((t: any) => t.key === formData.team_uuid);
      if (team?.owner && !memberUsers.find((u: any) => u.key === team.owner?.key)) {
        memberUsers.push(team.owner);
      }
      operators = memberUsers;
    } else if (isAdmin) {
      // If no team and admin/staff, return all users
      operators = allUsers;
    }
    
    // Always include current stream's operator if it exists and isn't already in the list
    // This ensures the select shows the correct value even before data loads
    if (stream.operator && !operators.find((u: any) => u.key === stream.operator?.key)) {
      operators = [stream.operator, ...operators];
    }
    
    return operators;
  };

  // Get available creators based on context
  const getAvailableCreators = () => {
    let creators: any[] = [];
    
    if (formData.team_uuid) {
      // If team selected, creator can be user himself or team members
      const memberUsers = teamMembers.map((role: any) => role.user).filter(Boolean);
      const team = availableTeams.find((t: any) => t.key === formData.team_uuid);
      if (team?.owner && !memberUsers.find((u: any) => u.key === team.owner?.key)) {
        memberUsers.push(team.owner);
      }
      // Always include current user (admin) as option
      if (currentUser && !memberUsers.find((u: any) => u.key === currentUser.key)) {
        memberUsers.push(currentUser);
      }
      creators = memberUsers;
    } else {
      // If no team selected, creator can be user himself or selected operator
      creators = [currentUser].filter(Boolean);
      if (formData.operator_uuid && formData.operator_uuid !== currentUser?.key) {
        const operator = allUsers.find((u: any) => u.key === formData.operator_uuid);
        if (operator && !creators.find((u: any) => u.key === operator.key)) {
          creators.push(operator as any);
        }
      }
    }
    
    // Always include current stream's creator if it exists and isn't already in the list
    // This ensures the select shows the correct value even before data loads
    if (stream.creator && !creators.find((u: any) => u.key === stream.creator?.key)) {
      creators = [stream.creator, ...creators];
    }
    
    return creators;
  };

  const availableOperators = getAvailableOperators();
  const availableCreators = getAvailableCreators();

  // Determine what data we need to load based on context
  const needsTeamRoles = !!formData.team_uuid;
  const needsAllUsers = isAdmin && !formData.team_uuid;
  
  // Check if we're still loading required data
  // For admin users, we always need to wait for teams query (to know if we need team roles or all users)
  // Then wait for either team roles (if team selected) or all users (if no team)
  const isLoadingRequiredData = (isAdmin && isLoadingTeams) ||
    (needsTeamRoles && isLoadingTeamRoles) || 
    (needsAllUsers && isLoadingAllUsers);
  
  // Check if creator Select should be shown (wait for queries to load and ensure creator exists in list)
  const streamCreatorExists = stream.creator?.key && 
    availableCreators.some((u: any) => u.key === stream.creator?.key);
  const shouldShowCreatorSelect = isAdmin && 
    !isLoadingRequiredData && 
    availableCreators.length > 0 && 
    streamCreatorExists;

  // Update form data when stream prop changes (but don't set creator_uuid here - it will be set by the other useEffect)
  useEffect(() => {
    setFormData(prev => ({
      name: stream.name || '',
      status: stream.status || 'WAITING',
      notes: stream.notes || '',
      ai_theme_plot: stream.ai_theme_plot || '',
      team_uuid: stream.team?.key,
      operator_uuid: stream.operator?.key,
      creator_uuid: prev.creator_uuid, // Keep existing creator_uuid, will be updated by the other useEffect
      start_date: stream.start_date || undefined,
      end_date: stream.end_date || undefined,
    }));
    setHasChanges(false);
    setErrors({});
  }, [stream]);

  // Check for changes when form data updates
  useEffect(() => {
    const hasModifications = 
      formData.name !== (stream.name || '') ||
      formData.status !== (stream.status || 'WAITING') ||
      formData.notes !== (stream.notes || '') ||
      formData.ai_theme_plot !== (stream.ai_theme_plot || '') ||
      formData.team_uuid !== stream.team?.key ||
      formData.operator_uuid !== stream.operator?.key ||
      formData.creator_uuid !== stream.creator?.key ||
      formData.start_date !== (stream.start_date || undefined) ||
      formData.end_date !== (stream.end_date || undefined);
    setHasChanges(hasModifications);
  }, [formData, stream]);

  // Reset operator when team changes
  useEffect(() => {
    if (formData.team_uuid) {
      // When team is selected, ensure operator is valid
      if (formData.operator_uuid && !availableOperators.find((u: any) => u.key === formData.operator_uuid)) {
        setFormData(prev => ({ ...prev, operator_uuid: currentUser?.key }));
      }
    } else {
      // When no team, default operator to creator if not admin
      if (!isAdmin && formData.operator_uuid !== currentUser?.key) {
        setFormData(prev => ({ ...prev, operator_uuid: currentUser?.key }));
      }
    }
  }, [formData.team_uuid, availableOperators.length, isAdmin, currentUser]);

  // Update available creators when operator changes (for no-team case)
  useEffect(() => {
    if (!formData.team_uuid && isAdmin) {
      const creators = [currentUser].filter(Boolean);
      if (formData.operator_uuid && formData.operator_uuid !== currentUser?.key) {
        const operator = allUsers.find((u: any) => u.key === formData.operator_uuid);
        if (operator && !creators.find((u: any) => u.key === operator.key)) {
          creators.push(operator as any);
        }
      }
      if (formData.creator_uuid && !creators.find((u: any) => u.key === formData.creator_uuid)) {
        setFormData(prev => ({ ...prev, creator_uuid: currentUser?.key }));
      }
    }
  }, [formData.operator_uuid, formData.team_uuid, allUsers, currentUser, isAdmin]);

  // Set creator_uuid only after verifying it exists in availableCreators and data is loaded
  useEffect(() => {
    // Skip if not admin
    if (!isAdmin) return;
    
    // Wait for required queries to finish loading
    if (isLoadingRequiredData) return;
    
    // If stream has a creator, verify it exists in availableCreators and set it
    if (stream.creator?.key) {
      const creatorExists = availableCreators.some((u: any) => u.key === stream.creator?.key);
      if (creatorExists) {
        // Only update if current value is different or not set
        if (formData.creator_uuid !== stream.creator.key) {
          setFormData(prev => ({ ...prev, creator_uuid: stream.creator?.key }));
        }
      }
    }
  }, [
    isLoadingRequiredData,
    availableCreators,
    stream.creator?.key,
    formData.creator_uuid,
    isAdmin
  ]);

  // Validation function
  const validateForm = (): FormErrors => {
    const newErrors: FormErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Experiment name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Experiment name must be at least 2 characters long';
    } else if (formData.name.trim().length > 512) {
      newErrors.name = 'Experiment name cannot exceed 512 characters';
    }

    // Status validation
    if (!formData.status) {
      newErrors.status = 'Status is required';
    } else if (!STREAM_STATUSES.some(s => s.value === formData.status)) {
      newErrors.status = 'Invalid status selected';
    }

    // Notes validation (optional, but check length if provided)
    if (formData.notes && formData.notes.length > 5000) {
      newErrors.notes = 'Notes cannot exceed 5000 characters';
    }
    if (formData.ai_theme_plot && formData.ai_theme_plot.length > 5000) {
      newErrors.ai_theme_plot = 'AI theme guidance cannot exceed 5000 characters';
    }

    return newErrors;
  };

  // Update stream mutation
  const updateStreamMutation = useMutation({
    mutationFn: (data: StreamUpdateData) => teamApi.updateStream(stream.key, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stream', stream.key] });
      queryClient.invalidateQueries({ queryKey: ['streams'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      setHasChanges(false);
    },
    onError: (error: any) => {
      console.error('Failed to update stream:', error);
    },
  });

  const handleInputChange = (field: keyof StreamUpdateData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleSelectChange = (field: keyof StreamUpdateData) => (
    event: any
  ) => {
    const value = event.target.value === '' ? undefined : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear error for this field when user changes selection
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const [isWithdrawDialogOpen, setIsWithdrawDialogOpen] = useState(false);

  const submitForm = () => {
    setErrors({});
    updateStreamMutation.mutate({
      name: formData.name.trim(),
      status: formData.status,
      notes: formData.notes?.trim() || undefined,
      ai_theme_plot: formData.ai_theme_plot?.trim() || undefined,
      team_uuid: formData.team_uuid || undefined,
      operator_uuid: formData.operator_uuid || undefined,
      creator_uuid: formData.creator_uuid || undefined,
      start_date: formData.start_date || undefined,
      end_date: formData.end_date || undefined,
    });
  };

  const handleSave = () => {
    if (!hasChanges) return;
    
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }
    
    if (formData.status === 'WITHDRAW' && stream.status !== 'WITHDRAW') {
      setIsWithdrawDialogOpen(true);
      return;
    }

    submitForm();
  };

  const handleReset = () => {
    setFormData({
      name: stream.name || '',
      status: stream.status || 'WAITING',
      notes: stream.notes || '',
      ai_theme_plot: stream.ai_theme_plot || '',
      team_uuid: stream.team?.key,
      operator_uuid: stream.operator?.key,
      creator_uuid: undefined, // Will be set by useEffect after verifying it exists in availableCreators
      start_date: stream.start_date || undefined,
      end_date: stream.end_date || undefined,
    });
    setHasChanges(false);
    setErrors({});
  };

  // Don't render the form until required data is loaded
  if (isLoadingRequiredData) {
    return (
      <Box sx={{ p: 3, maxWidth: 800, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 800 }}>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
        Experiment Settings
      </Typography>

      <Card>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Experiment Name */}
            <TextField
              fullWidth
              label="Experiment Name"
              variant="outlined"
              value={formData.name}
              onChange={handleInputChange('name')}
              disabled={updateStreamMutation.isPending}
              error={!!errors.name}
              helperText={errors.name || "The display name for this stream"}
              required
            />

            {/* Stream Status */}
            <FormControl fullWidth variant="outlined" error={!!errors.status}>
              <InputLabel id="stream-status-label">Status *</InputLabel>
              <Select
                labelId="stream-status-label"
                label="Status *"
                value={formData.status}
                onChange={handleSelectChange('status')}
                disabled={updateStreamMutation.isPending}
              >
                {STREAM_STATUSES.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.status && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  {errors.status}
                </Typography>
              )}
            </FormControl>

            {/* Team Selection - only show if user has teams */}
            {hasTeams && (
              <FormControl fullWidth>
                <InputLabel id="team-label">Team (Optional)</InputLabel>
                <Select
                  labelId="team-label"
                  label="Team (Optional)"
                  value={formData.team_uuid || ''}
                  onChange={handleSelectChange('team_uuid')}
                  disabled={updateStreamMutation.isPending}
                >
                  <MenuItem value="">None</MenuItem>
                  {availableTeams.map((team: any) => (
                    <MenuItem key={team.key} value={team.key}>
                      {team.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Creator Selection - only for admin/staff */}
            {shouldShowCreatorSelect && (
              <FormControl fullWidth>
                <InputLabel id="creator-label">Creator</InputLabel>
                <Select
                  labelId="creator-label"
                  label="Creator"
                  value={formData.creator_uuid || ''}
                  onChange={handleSelectChange('creator_uuid')}
                  disabled={updateStreamMutation.isPending}
                >
                  {availableCreators.map((user: any) => (
                    <MenuItem key={user.key} value={user.key}>
                      {user.name || user.email} {user.key === currentUser?.key ? '(You)' : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Operator Selection - show if team selected or if admin/staff */}
            {(formData.team_uuid || isAdmin) && availableOperators.length > 0 && (
              <FormControl fullWidth>
                <InputLabel id="operator-label">Operator</InputLabel>
                <Select
                  labelId="operator-label"
                  label="Operator"
                  value={formData.operator_uuid || currentUser?.key || ''}
                  onChange={handleSelectChange('operator_uuid')}
                  disabled={updateStreamMutation.isPending}
                >
                  {availableOperators.map((user: any) => (
                    <MenuItem key={user.key} value={user.key}>
                      {user.name || user.email} {user.key === currentUser?.key ? '(You)' : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Start Date and End Date */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={formData.start_date || ''}
                onChange={handleInputChange('start_date')}
                disabled={updateStreamMutation.isPending}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
              />
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={formData.end_date || ''}
                onChange={handleInputChange('end_date')}
                disabled={updateStreamMutation.isPending}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
              />
            </Box>

            {/* Stream Notes */}
            <TextField
              fullWidth
              label="Notes"
              variant="outlined"
              multiline
              rows={10}
              value={formData.notes}
              onChange={handleInputChange('notes')}
              disabled={updateStreamMutation.isPending}
              error={!!errors.notes}
              helperText={errors.notes || "Additional notes about this stream"}
              placeholder="Enter any notes about this stream..."
            />

            <TextField
              fullWidth
              label="AI Theme / Plot Guidance"
              variant="outlined"
              multiline
              rows={5}
              value={formData.ai_theme_plot || ''}
              onChange={handleInputChange('ai_theme_plot')}
              disabled={updateStreamMutation.isPending}
              error={!!errors.ai_theme_plot}
              helperText={
                errors.ai_theme_plot ||
                "Optional secondary guidance for AI signal detection and strategy writing (e.g. prioritize meeting-booking vs referral-seeking tone when evidence is otherwise close)."
              }
              placeholder="Example: This stream should generally steer toward booking a discovery call, while staying aligned with the prospect's explicit intent."
            />
          </Box>

          {/* Error Display */}
          {updateStreamMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Failed to update experiment settings. Please try again.
            </Alert>
          )}

          {/* Success Display */}
          {updateStreamMutation.isSuccess && !hasChanges && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Experiment settings updated successfully!
            </Alert>
          )}
        </CardContent>

        <CardActions sx={{ p: 3, pt: 0, gap: 2 }}>
          <Button
            variant="contained"
            startIcon={
              updateStreamMutation.isPending ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            onClick={handleSave}
            disabled={!hasChanges || updateStreamMutation.isPending}
            sx={{ minWidth: 120 }}
          >
            {updateStreamMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>

          <Button
            variant="outlined"
            onClick={handleReset}
            disabled={!hasChanges || updateStreamMutation.isPending}
          >
            Reset
          </Button>
        </CardActions>
      </Card>

      {/* Withdraw Confirmation Dialog */}
      <Dialog
        open={isWithdrawDialogOpen}
        onClose={() => setIsWithdrawDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Withdraw all pending invites</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1">
            This will withdraw all pending connection invites for this experiment.
            A background job will process each pending invite and withdraw it.
            Once the withdrawal is complete, the experiment status will be set to <strong>Done</strong>.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone. Are you sure you want to proceed?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsWithdrawDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              setIsWithdrawDialogOpen(false);
              submitForm();
            }}
            disabled={updateStreamMutation.isPending}
          >
            Withdraw invites
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
